import type { Attachment } from 'ai';

/**
 * Client-side preparation of a user-picked image for the AI chat request.
 *
 * The picked file is decoded and downscaled into a JPEG data URL before it
 * rides the chat message (`experimental_attachments`): vision models cap
 * useful resolution around ~1.5k px on the long edge, serverless request
 * bodies cap out near 4.5 MB, and re-encoding normalizes camera formats the
 * providers reject (e.g. HEIC on Safari) into a universally accepted type.
 * Files the browser cannot decode are passed through untouched only while
 * they stay under the raw-size cap; anything bigger throws ImageTooLargeError.
 */

const MAX_DIMENSION = 1568;
const JPEG_QUALITY = 0.85;
// Fallback dimensions/quality if the first encode is still too heavy.
const RETRY_DIMENSION = 1024;
const RETRY_QUALITY = 0.7;
// Cap for the encoded data URL / raw pass-through (headroom under the
// serverless body limit once the JSON envelope is added).
const MAX_ENCODED_CHARS = 3 * 1024 * 1024;

export class ImageTooLargeError extends Error {
  constructor() {
    super('image-too-large');
    this.name = 'ImageTooLargeError';
  }
}

function readAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('read-failed'));
    reader.readAsDataURL(file);
  });
}

function decodeViaImageElement(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('decode-failed'));
    };
    img.src = url;
  });
}

async function decodeImage(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch {
      // Fall through to the <img> decoder (some browsers reject formats here
      // that they can still decode via an image element).
    }
  }
  return decodeViaImageElement(file);
}

function encodeToJpeg(
  source: ImageBitmap | HTMLImageElement,
  maxDimension: number,
  quality: number,
): string | null {
  const sourceWidth = 'naturalWidth' in source ? source.naturalWidth : source.width;
  const sourceHeight = 'naturalHeight' in source ? source.naturalHeight : source.height;
  if (!sourceWidth || !sourceHeight) return null;

  const scale = Math.min(1, maxDimension / Math.max(sourceWidth, sourceHeight));
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  // JPEG has no alpha channel — transparent regions would otherwise turn black.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(source, 0, 0, width, height);

  const dataUrl = canvas.toDataURL('image/jpeg', quality);
  // A canvas tainted or an unsupported encoder returns "data:,"
  return dataUrl.startsWith('data:image/') ? dataUrl : null;
}

/**
 * Turn a picked File into a chat `Attachment` (data-URL based), downscaled and
 * re-encoded as JPEG whenever the browser can decode it.
 *
 * @throws ImageTooLargeError when the image cannot be brought under the size cap.
 */
export async function prepareImageAttachment(file: File): Promise<Attachment> {
  const baseName = (file.name || 'image').replace(/\.[a-z0-9]+$/i, '');

  let decoded: ImageBitmap | HTMLImageElement | null = null;
  try {
    decoded = await decodeImage(file);
  } catch {
    decoded = null;
  }

  if (decoded) {
    let dataUrl = encodeToJpeg(decoded, MAX_DIMENSION, JPEG_QUALITY);
    if (dataUrl && dataUrl.length > MAX_ENCODED_CHARS) {
      dataUrl = encodeToJpeg(decoded, RETRY_DIMENSION, RETRY_QUALITY);
    }
    if ('close' in decoded) decoded.close();
    if (dataUrl) {
      if (dataUrl.length > MAX_ENCODED_CHARS) throw new ImageTooLargeError();
      return { name: `${baseName}.jpg`, contentType: 'image/jpeg', url: dataUrl };
    }
  }

  // Undecodable format: pass the original bytes through while small enough.
  if (file.size > (MAX_ENCODED_CHARS / 4) * 3) throw new ImageTooLargeError();
  const rawUrl = await readAsDataURL(file);
  if (rawUrl.length > MAX_ENCODED_CHARS) throw new ImageTooLargeError();
  return {
    name: file.name || 'image',
    contentType: file.type || 'image/jpeg',
    url: rawUrl,
  };
}
